import React, { useCallback, useEffect, useRef, useState } from "react";
import { createTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  LinearProgress,
  Typography,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  formatDate,
  getAppsBuildings,
  useInternetStatusDetector,
  setLocalStorageItem,
  getLocalStorageItem,
} from "../../_helpers";
import { surveyActions } from "../../_actions";
import { useRedirectionPath } from "../../_helpers/redirectHelper";
import { SurveyType, WKB_API_INTERVAL_TIME } from "../../_helpers/enumsHelper";
import { apps, LOCAL_STORAGE_GRID_KEYS, surveyConstants } from "../../_constants";
import { SurveyingMomentCustomRow } from "./components/SurveyingMomentCustomRow";
import SelectSingleObjectModal from "../../components/SelectSingleObjectModal";
import DataGridV2 from "../../components/DataGridV2";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { INITIAL_ROW_SELECTION_MODEL } from "../../components/DataGridV2/constants";
import { getSurveyMomentColumns } from "./surveyMomentUtils";

export function NumberLinearProgress(props) {
  const classes = useStyles();
  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <LinearProgress variant="determinate" value={props.value} />
      <Typography
              className={classes.progressStyle}
            >{`${props.value}%`}</Typography>
    </Box>
  );
}

const SurveyMoments = (props) => {
  const { history } = props;
  const { t , i18n} = useTranslation();
  const apiRef = useGridApiRef();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles({});
  const ref = useRef(null);
  //Redux State
  const selectedApp = useSelector((state) => state.app);
  const { user } = useSelector((state) => state.authentication);
  const { selected } = useSelector((state) => state.buildings);
  const { surveys, getSurveysLoading } = useSelector(
    (state) => state.surveying
  );
  const { availableSurveyModules } = useSelector(
    (state) => state.availableSurveyModules
  );

  const dispatch = useDispatch();
  const redirectPatch = useRedirectionPath(SurveyType.WKB);
  const [intervalId, setIntervalId] = useState(null);
  //Online/offline detector
  const isOnline = useInternetStatusDetector();
  const app = useSelector((state) => state.app);
   const keyPrefix = app === apps.aftercare ? "AFTERCARE" : app === apps.huisinfo ? "BUYERS_GUIDE" : app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";
   const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
           LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_PAGINATION[keyPrefix], undefined
    ))
   const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL); 

  const rows = surveys.map((s) => {
  const startDate = s.startDate ? formatDate(new Date(s.startDate), false, "-") : "";
  const endDate = s.endDate ? formatDate(new Date(s.endDate), false, "-") : "";
  return {
    ...s,
    id: s.surveyingMomentId,
    responsiblePersonName: s.responsiblePerson?.name || "",
    responsiblePersonId: s.responsiblePerson?.loginId || null,
    handlingPeriod: `${startDate} - ${endDate}`,
    assignments: `${s.completedAssignments }/${s.totalAssignments }`,
    relationNameId: s.responsiblePerson?.loginId || null,
  };
  });


  useEffect(() => {
    const id = setInterval(timer, WKB_API_INTERVAL_TIME);
    setIntervalId(id);
    return () => {
      clearInterval(intervalId);
      if (!history.location.pathname.includes("toezichtsmomenten")) {
        dispatch(
          surveyActions.success({
            type: surveyConstants.GET_SURVEYS_MOMENTS_SUCCESS,
            surveys: [],
          })
        );
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      clearInterval(intervalId);
    };
  }, [intervalId]);

  const timer = () => {
    if (selected && selected.projectId) {
      dispatch(
        surveyActions.getSurveyingMoments(
          selected.projectId,
          selectedApp,
          false
        )
      );
    }
  };



  const [isObjectModal, setIsObjectModal] = useState(false);
  const [mappedState, setMappedState] = useState({
    selectedBuildingId: null,
    selectedObjectId: null,
  });

  const objectModalListHandler = (
    event,
    selectedBuildingId,
    selectedObjectId
  ) => {
    event.stopPropagation();
    setIsObjectModal(true);
    setMappedState({ selectedBuildingId, selectedObjectId });
  };

  const handleCloseObjects = () => {
    setIsObjectModal(false);
    setMappedState({ selectedBuildingId: null, selectedObjectId: null });
  };

  const handleSaveObjects = (buildingId) => {
    dispatch(
      surveyActions.mapBuildingObject({
        objectId: mappedState.selectedObjectId,
        buildingId,
      })
    );
    handleCloseObjects();
  };
  const [all, setAll] = useState([]);

  useEffect(() => {
    if (selected) {
      getAppsBuildings({ projectId: selected.projectId || "" })
        .then(async (res) => {
          setAll(res);
        })
        .catch((er) => {
          console.log(er);
        });
    }
  }, [selected]);
  


  const relationNameOptions = [
    ...new Set(
      rows
        .filter(r => r?.responsiblePerson?.loginId && r?.responsiblePerson?.relationName)
        .map(r => JSON.stringify({ value: r.responsiblePerson.loginId, label: r.responsiblePerson.relationName }))
    )
  ].map(str => JSON.parse(str));

  
  const responsiblePersonOptions = [
    ...new Set(
      rows
        .filter(r => r?.responsiblePerson?.loginId && r?.responsiblePersonName)
        .map(r => JSON.stringify({ value: r?.responsiblePerson?.loginId, label: r?.responsiblePersonName }))
    )
  ].map(str => JSON.parse(str));
  
  
  const columns = getSurveyMomentColumns({ t, keyPrefix, i18n, apiRef, theme, classes, objectModalListHandler, responsiblePersonOptions,relationNameOptions })

  useEffect(() => {
    if (redirectPatch) history.push(redirectPatch);
  }, [availableSurveyModules, user]);


  useEffect(() => {
    if (selected && selected.projectId) {
      dispatch(
        surveyActions.getSurveyingMoments(selected.projectId, selectedApp, true)
      );
    }
  }, [selected, history.location, isOnline]);



  const getSurveyingMomentsForPrintAndDownload = ({ isAllSelected = true, idsToExport = [], }) => {
    const userSelectedRows = isAllSelected ? surveys : idsToExport.map((id) => surveys.find((item) => item?.surveyingMomentId === id));
    const results = userSelectedRows?.map((surveyingMoment) => {
      return columns.reduce((obj, col) => {
        const field = col.field;
        const value = surveyingMoment[field];
        switch (field) {
          case "extraInformation": {
            const array = [];

            if (surveyingMoment.hasUpdate) {
              array.push({ icon: "PriorityHigh", color: "secondary" });
            }

            if (surveyingMoment.objectId && !surveyingMoment.isBuildingMapped) {
              array.push({ icon: "ErrorOutline", color: "error" });
            }

            if (
              surveyingMoment.is48hoursReminder ||
              surveyingMoment.overdue ||
              surveyingMoment.wbStatus ||
              surveyingMoment.isBlocked
            ) {
              surveyingMoment.wbStatus
                ? array.push({ icon: "CheckCircle", color: "success" })
                : surveyingMoment.isBlocked
                  ? array.push({ icon: "Block", color: "error" })
                  : array.push({ icon: "Schedule", color: "error" });
            }

            array.push({
              icon: "Message",
              count: surveyingMoment.messageCount,
            });
            obj[field] = array;
            break;
          }

          case "startDate": {
            const startDate = surveyingMoment.startDate
              ? formatDate(new Date(surveyingMoment.startDate), false, "-")
              : "";
            const endDate = surveyingMoment.endDate
              ? formatDate(new Date(surveyingMoment.endDate), false, "-")
              : "";
            obj[field] = `${startDate} - ${endDate}`;
            break;
          }

          case "assignments": {
            const totalAssignments = surveyingMoment.totalAssignments;
            const completedAssignments = surveyingMoment.completedAssignments;
            obj[field] = `${totalAssignments}/${completedAssignments}`;
            break;
          }

          case "progress": {
            const { totalAssignments, completedAssignments } =
              surveyingMoment || {};
            obj[field] = `${(
              (completedAssignments / totalAssignments) *
              100
            ).toFixed(2)}%`;
            break;
          }

          case "responsiblePersonId": {
            const personName = surveyingMoment?.responsiblePerson;
            obj[field] = personName ? personName.name : "";
            break;
          }

          case "relationNameId": {
            const person = surveyingMoment.responsiblePerson;
            obj[field] = person ? person.relationName : "";
            break;
          }

          case "internalStatusPlainText": {
            obj[field] = t(surveyingMoment.internalStatusPlainText);
            break;
          }

          default: {
            if (col.type === "date" && surveyingMoment[field]) {
              obj[field] = formatDate(new Date(surveyingMoment[field]), false, "-");
            } else {
              obj[field] = value ?? "";
            }
            break;
          }
        }
        return obj;
      }, {});
    });
    return results;
  }

  const handleRowClick = (selectedRow) => {
  history.push({
    pathname: props.match.url.replace("toezichtsmomenten", "opdrachten"),
    state: {
      filters: [
        {
          field: "moment",       
          operator: "equals",    
          value: selectedRow.name,
        },
        {
          field: "object",
          operator: "equals",
          value: selectedRow.object,
        },
      ],
      canGoBack: true,
    },
  });
};


 
 
  const onPaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel)
    setLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_PAGINATION[keyPrefix],
      newPaginationModel
    );
  }, [keyPrefix])

  const handleCheckboxChange = useCallback((itemId) => {
    setRowSelectionModel((prevCheckedItems) => {
      const prevIds = Array.from(prevCheckedItems?.ids);
      let newIds = [];
      if (prevIds.includes(itemId)) {
        newIds = prevIds.filter((id) => id !== itemId);
      } else {
        newIds = [...prevIds, itemId];
      }
      return { ...prevCheckedItems, ids: new Set(newIds) };
    });
  }, []);

  return (
    <div className={classes.scrollContainer}>
      <Container ref={ref} maxWidth={false} className={classes.mainContainer}>
        <Box display={'flex'} flexDirection={'column'} height={'100%'}>
          <Box p={2} >
            <Typography className={classes.headerTitle} variant='h6'>{t("wkb.surveyingMoment")}</Typography>
          </Box>
          <Box sx={{ flex: 1 , minHeight:0}}>
            <DataGridV2
              MobileCustomRowComponent={(props) => {
                return <SurveyingMomentCustomRow
                  selectedCheckBoxes={Array.from(rowSelectionModel?.ids)}
                  handleCheckboxChange={handleCheckboxChange}
                  onRowClick={handleRowClick}
                  item={props?.row}
                  key={props.rowId}
                />
              }}
              localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_SEARCH_TEXT[keyPrefix]}
              localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_COLUMN_FILTER[keyPrefix]}
              localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_COLUMN_SORT[keyPrefix]}
              localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_COLUMN_ORDER[keyPrefix]}
              localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_COLUMN_WIDTH[keyPrefix]}
              localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_COLUMN_VISIBILITY[keyPrefix]}
              localDataGridViewIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_MOMENTS_DATAGRID_VIEW[keyPrefix]}
              apiRef={apiRef}
              checkboxSelection
              rows={rows}
              columns={columns}
              loading={getSurveysLoading}
              pagination
              filterMode="client"
              customRowHeight={isMobile ? 80 : 62}
              {...(!!paginationModel && { paginationModel })}
              onPaginationModelChange={onPaginationModelChange}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(newRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
              }}
              toolbarProps={{
                selectedRows: rowSelectionModel,
                getData: getSurveyingMomentsForPrintAndDownload,
                title: t('wkb.surveyingMoment'),

              }}
              onRowClick={(params) => {
                const surveyingMomentId = params?.row?.surveyingMomentId;
                const selectedRow =
                  surveys &&
                  surveys.find((x) => x.surveyingMomentId === surveyingMomentId);
                if (!!selectedRow) {
                  handleRowClick(selectedRow);
                }
              }}

            />
            {isObjectModal && (
              <SelectSingleObjectModal
                open={isObjectModal}
                buildings={all}
                canProjectLevel
                selectedBuilding={mappedState.selectedBuildingId}
                onSave={handleSaveObjects}
                onClose={handleCloseObjects}
              />
            )}
          </Box>
        </Box>
      </Container>
    </div>
  );
};
export default SurveyMoments;
const theme = createTheme({
    palette: {
      primary: {
        main: "#2DA800",
      },
    },
    components: {
      MuiLinearProgress: {
        styleOverrides: {
        root: {
          borderRadius: 50,
          height: 15,
        },
        bar1Determinate: {
          borderRadius: 4,
        },
        colorPrimary: {
          backgroundColor: "#9b9b9b",
        },
      },
    },
    },
  }
);

const useStyles = makeStyles((theme) => ({
  "@global": {
    ".MuiButtonBase-root:focus": {
      outline: 0,
    },
  },
progressStyle: {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#fff",
},
  warning: {
    fill: "#ffc107",
  },
  pending: {
    fill: "#dc3545",
  },
  colorSuccess: {
    color: theme.palette.success.main,
  },
  progressWrapper: {
    position: "relative",
  color: theme.palette.grey[800],
  [theme.breakpoints.only("xs")]: {
      width: "100%",
    },
    [theme.breakpoints.only("sm")]: {
      width: "100%",
    },
},

  customMainHeader: {
    fontSize: 12,
    "& .MuiButtonBase-root": {
      [theme.breakpoints.only("xs")]: {
        fontSize: 14,
      },
      [theme.breakpoints.only("sm")]: {
        fontSize: 16,
      },
      fontSize: 14,
      padding: 0,
      margin: 0,
      whiteSpace: "nowrap ",
    },
  },
  tableIcon: {
    fontSize: 20,
    boxShadow: "none",
  },
  messageCountIcon: {
    "& span": {
      [theme.breakpoints.down("md")]: {
        fontSize: 9,
        height: 14,
        minWidth: 14,
        padding: theme.spacing(0, 0.4),
      },
    },
  },
  iconContainer: {
    [theme.breakpoints.only("sm")]: {
      minWidth: 20,
    },
    [theme.breakpoints.only("xs")]: {
      minWidth: 15,
    },
    "& svg": {
      fontSize: 16,
    },
    minWidth: 32,
    display: "flex",
    justifyContent: "center",
  },
  menuContent: {
    "& .MuiPopover-paper": {
      borderRadius: 10,
      boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px !important",
    },
    "& ul": {
      padding: theme.spacing(0),
    },
    "& ul > ul": {
      padding: theme.spacing(2),
    },
  },
  menuListStyle: {
    padding: theme.spacing(1.875, 1.25),
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
    borderRadius: 10,
    maxWidth: 325,
    width: "fit-content",
    minWidth: 200,
  },

  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "auto",
    padding: theme.spacing(0),
    background: theme.palette.common.white,
  },
  scrollContainer: {
    height: "100%",
    padding: theme.spacing(0),
    overflow: "auto",
  },

  customTableCell: {
    [theme.breakpoints.only("xs")]: {
      fontSize: 14,
    },
    [theme.breakpoints.only("sm")]: {
      fontSize: 16,
    },
    [theme.breakpoints.up("sm")]: {
      whiteSpace: "nowrap",
    },
    fontSize: "14px ",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  iconButton: {
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(0),
    },
    padding: theme.spacing(0.45, 0.5),
  },
  colorWarning: {
    color: theme.palette.error.main,
    fontSize: 16,
  },
  content: {
    position: "absolute",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  spacer: {
    height: 0,
    overflow: "hidden",
  },
}));
